import type { TargetingProviderConfig } from '@openx/types/targeting/targetingProviderConfig.ts';
import { useAdPlacementOptionsFetch } from '@openx/utils/apiHooks/useAdPlacementOptionsFetch.ts';
import { useBlockIrrelevantTargetingSchemaFetch } from '@openx/utils/apiHooks/useBlockIrrelevantTargetingSchemaFetch.ts';
import { useCountriesOptionsFetch } from '@openx/utils/apiHooks/useCountriesOptionsFetch.ts';
import { useDeviceTypesOptionsFetch } from '@openx/utils/apiHooks/useDeviceTypesOptionsFetch.ts';
import { useDistributionChannelFetch } from '@openx/utils/apiHooks/useDistributionChannelOptionsFetch.ts';
import { useOldAdPlacementOptionsFetch } from '@openx/utils/apiHooks/useOldAdPlacementOptionsFetch.ts';
import { useOldDistributionChannelFetch } from '@openx/utils/apiHooks/useOldDistributionChannelOptionsFetch.ts';
import { useAccountUid } from './InventoryAndContent/useAccountUid.ts';
import { useCurrentInstanceState } from './InventoryAndContent/useCurrentInstanceState.ts';
import { useFetchInventoriesBySearchText } from './InventoryAndContent/useFetchInventoriesBySearchText.ts';
import { useFetchInventoryInstances } from './InventoryAndContent/useFetchInventoryInstances.ts';
import { useFetchInventoryMarket } from './InventoryAndContent/useFetchInventoryMarket.ts';
import { useInventoriesFetch } from './InventoryAndContent/useInventoriesFetch.ts';
import { usePmpSizesFetch } from './InventoryAndContent/usePmpSizesFetch.ts';
import { useAccountFetch } from './useAccountFetch.ts';
import { useAudienceOptionsDetailsFetch } from './useAudienceOptionsDetailsFetch.ts';
import { useAudienceOptionsFetch } from './useAudienceOptionsFetch.ts';
import { useCategoriesFetch } from './useCategoriesFetch.ts';
import { useContentObjectOptionsConfig } from './useContentObjectOptionsConfig.ts';
import { useExchangeSizeFetch } from './useExchangeSizeOptions.ts';
import { useGetAudienceMultiproviderInfo } from './useGetAudienceMultiproviderInfo.ts';
import { useInventoryListByIdFetch } from './useInventoryListByIdFetch.ts';
import { useInventoryListsOptionsFetch } from './useInventoryListsOptionsFetch.ts';
import { useKeywordsOptionsFetch } from './useKeywordsOptionsFetch.ts';
import { useLocationSourceOptionsFetch } from './useLocationSourceOptionsFetch.ts';
import { useMetacategoriesFetch } from './useMetacategoriesFetch.ts';
import { usePostalCodes } from './usePostalCodes.ts';
import { useFetchByGeoData, useFetchBySearchText } from './useSearchGeoLocations.ts';
import { useSelectRateOptionsFetch } from './useSelectRateOptionsFetch.ts';
import { useTechnologyOptionsFetch } from './useTechnologyOptionsFetch.ts';
import { useTrafficFilterEidsOptionsFetch } from './useTrafficFilterEidsOptionsFetch.ts';
import { useVideoOptionsFetch } from './useVideoOptionsFetch.ts';

const todoConfig = {
  useAccountOptionsFetch: () => {
    throw new Error('useAccountOptionsFetch not implemented.');
  },
  useFetchSegments: () => {
    throw new Error('useFetchSegments not implemented.');
  },
  useFetchSegmentsV2: () => {
    throw new Error('useFetchSegmentsV2 not implemented.');
  },
  useContextualDataPartnerOptions: () => ({ dataPartnerOptions: [], dataPartnerOptionsLoading: false }),
  useRelatedAccountsFetch: () => {
    throw new Error('useRelatedAccountsFetch not implemented.');
  },
  useInventoryListsOptionsFetch: () => {
    throw new Error('useInventoryListsOptionsFetch not implemented.');
  },
  useInventoryListByIdFetch: () => {
    throw new Error('useInventoryListByIdFetch not implemented.');
  },
  useAudienceCreateAndActivate: () => {
    throw new Error('useAudienceCreateAndActivate not implemented.');
  },
  useAudienceOptionsDetailsLazyFetch: () => {
    throw new Error('useAudienceOptionsDetailsLazyFetch not implemented.');
  },
};

export const targetingConfig: TargetingProviderConfig = {
  ...todoConfig,
  useAccountFetch,
  useAccountUid,
  useAdPlacementOptions: useAdPlacementOptionsFetch,
  useOldAdPlacementOptions: useOldAdPlacementOptionsFetch,
  useAudienceOptionsDetailsFetch,
  useAudienceOptionsFetch,
  useCategoriesFetch,
  useContentObjectOptionsConfig,
  useCountriesOptionsFetch,
  useCurrentInstanceState,
  useDeviceTypesOptionsFetch: useDeviceTypesOptionsFetch,
  useDistributionChannelOptions: useDistributionChannelFetch,
  useOldDistributionChannelOptions: useOldDistributionChannelFetch,
  useExchangeSizeFetch,
  useFetchByGeoData,
  useFetchBySearchText,
  useFetchInventoriesBySearchText,
  useFetchInventoryInstances,
  useFetchInventoryMarket,
  useInventoriesFetch,
  useKeywordsOptionsFetch,
  useLocationSourceOptionsFetch,
  useMetacategoriesFetch,
  usePmpSizesFetch,
  usePostalCodes,
  useSelectRateOptionsFetch,
  useTechnologyOptionsFetch,
  useTrafficFilterEidsOptionsFetch,
  useVideoOptionsFetch,
  useInventoryListByIdFetch,
  useInventoryListsOptionsFetch,
  useGetAudienceMultiproviderInfo,
  useTargetingBlockingRulesSchemaFetch: useBlockIrrelevantTargetingSchemaFetch,
};
